import memoizeOne from 'memoize-one';
import React, { Fragment, PureComponent } from 'react';
import { VirtualBrand, VirtualPack } from '~/Api/Analytics/VirtualBrand';
import { AnalyticDataSources } from '~/Api/Analytics/VirtualBrand/mapAnalyticVirtualBrand';
import ActivityIndicator from '~/UI/ActivityIndicator';
import AvailabilityTab, {
	TabState as AvailabilityTabState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand/AvailabilityTab';
import HistoryTab from '~/MedicinalBrandsAnalytics/VirtualBrand/HistoryTab';
import Message from '~/MedicinalBrandsAnalytics/VirtualBrand/Message';
import MessageStyles from '~/MedicinalBrandsAnalytics/VirtualBrand/Message/styles.less';
import PricesTab, { TabState as PricesTabState } from '~/MedicinalBrandsAnalytics/VirtualBrand/PricesTab';
import RegistrationTab from '~/MedicinalBrandsAnalytics/VirtualBrand/RegistrationTab';
import ReimbursementTab, {
	TabState as ReimbursementTabState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand/ReimbursementTab';
import TimeToPriceTab, {
	TabState as TimeToPriceTabState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand/TimeToPriceTab';
import Button from '~/UI/Button';
import Select from '~/UI/Select';
import TabBar, { Tab } from '~/UI/TabBar';

import ERPTab from './ERPTab/ERPTab';
import InfoTab from './InfoTab';
import styles from './styles.less';
import tabBarStyles from './tabBar.less';
import BrandDetailUrlState, { DEFAULT_TABNAME, TabStateName } from './BrandDetailUrlState';
import { hasERPModuleActive } from '~/Api/Authentication/access-token-utils';

export { TabStateName } from './BrandDetailUrlState';

type TabState =
	| { name: TabStateName.info; state: undefined }
	| { name: TabStateName.availability; state: AvailabilityTabState }
	| { name: TabStateName.timeToPrice; state: TimeToPriceTabState | null }
	| { name: TabStateName.prices; state: PricesTabState | null }
	| { name: TabStateName.reimbursement; state: ReimbursementTabState | null }
	| { name: TabStateName.registration; state: undefined }
	| { name: TabStateName.history; state: undefined }
	| { name: TabStateName.irp; state: undefined };

export type State = {
	tab: TabState;
	packId: string | null;
};

export const selectVirtualPack = memoizeOne((virtualPacks: VirtualPack[], id: string | null): VirtualPack | null => {
	return id ? virtualPacks.find((virtualPack) => virtualPack.id === id) || null : null;
});

export type Props = {
	fetchingVirtualBrandAnalytics: boolean;
	virtualBrand: VirtualBrand;
	virtualBrandAvailability: { [countryCode: string]: AnalyticDataSources } | null;
	virtualPackAvailability: { [virtualPackId: string]: { [countryCode: string]: AnalyticDataSources } } | null;
	currency: string | null;
	state: State;
	subscriptionModules: Set<string>;
	onStateChange(nextState: State): void;
	onLoad(virtualBrand: VirtualBrand): void;
};

export default class BrandDetail extends PureComponent<Props> {
	static defaultProps: {
		state: State;
	} = {
		state: {
			tab: { name: DEFAULT_TABNAME, state: undefined },
			packId: null,
		},
	};

	componentDidMount() {
		this.props.onLoad(this.props.virtualBrand);
		this.props.onStateChange(this.props.state);
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.virtualBrand.id !== prevProps.virtualBrand.id) {
			this.props.onLoad(this.props.virtualBrand);
		}
	}

	renderTab() {
		const {
			virtualBrand,
			virtualBrandAvailability,
			virtualPackAvailability,
			currency,
			state: { tab, packId },
			subscriptionModules,
		} = this.props;
		const selectedVirtualPack = selectVirtualPack(virtualBrand.virtualPacks, packId);
		const props = {
			virtualBrand,
			virtualBrandAvailability,
			virtualPackAvailability,
			state: tab.state,
			onStateChange: this.tabStateChanged,
		};

		if (!props.virtualBrandAvailability || !props.virtualPackAvailability) {
			return this.props.fetchingVirtualBrandAnalytics ? <ActivityIndicator /> : null;
		}

		switch (tab.name) {
			case TabStateName.info:
				return (
					<InfoTab
						currency={currency}
						virtualBrand={virtualBrand}
						virtualBrandAvailability={props.virtualBrandAvailability}
						virtualPack={selectedVirtualPack}
						virtualPackAvailability={props.virtualPackAvailability}
					/>
				);
			case TabStateName.availability:
				return (
					<AvailabilityTab
						state={tab.state}
						virtualBrand={props.virtualBrand}
						virtualBrandAvailability={props.virtualBrandAvailability}
						virtualPack={selectedVirtualPack}
						virtualPackAvailability={props.virtualPackAvailability}
						onStateChange={(state: AvailabilityTabState) => {
							props.onStateChange({
								name: TabStateName.availability,
								state,
							});
						}}
					/>
				);
			case TabStateName.irp:
				return hasERPModuleActive(subscriptionModules) ? (
					<ERPTab
						currency={currency}
						virtualBrandAvailability={props.virtualBrandAvailability}
						virtualBrandId={virtualBrand.id}
						virtualBrandName={virtualBrand.brandName}
						virtualPack={selectedVirtualPack}
						virtualPackAvailability={props.virtualPackAvailability}
					/>
				) : null;
			case TabStateName.timeToPrice:
				return (
					<TimeToPriceTab
						state={tab.state}
						virtualBrand={props.virtualBrand}
						virtualBrandAvailability={props.virtualBrandAvailability}
						virtualPack={selectedVirtualPack}
						virtualPackAvailability={props.virtualPackAvailability}
						onStateChange={(state: TimeToPriceTabState) => {
							props.onStateChange({
								name: TabStateName.timeToPrice,
								state,
							});
						}}
					/>
				);
			case TabStateName.prices:
				return (
					<PricesTab
						state={tab.state}
						virtualBrand={props.virtualBrand}
						virtualBrandAvailability={props.virtualBrandAvailability}
						virtualPack={selectedVirtualPack}
						virtualPackAvailability={props.virtualPackAvailability}
						onStateChange={(state: PricesTabState) => {
							props.onStateChange({
								name: TabStateName.prices,
								state,
							});
						}}
					/>
				);
			case TabStateName.reimbursement:
				return (
					<ReimbursementTab
						state={tab.state}
						virtualBrand={props.virtualBrand}
						virtualBrandAvailability={props.virtualBrandAvailability}
						virtualPack={selectedVirtualPack}
						virtualPackAvailability={props.virtualPackAvailability}
						onStateChange={(state: ReimbursementTabState) => {
							props.onStateChange({
								name: TabStateName.reimbursement,
								state,
							});
						}}
					/>
				);
			case TabStateName.registration:
				return <RegistrationTab virtualBrand={virtualBrand} />;
			case TabStateName.history:
				return <HistoryTab currency={currency} virtualBrand={virtualBrand} />;
			default:
				return null;
		}
	}

	tabStateChanged = (tabState: TabState) => {
		const { state, onStateChange } = this.props;
		onStateChange({ ...state, tab: { ...tabState } });
	};

	render() {
		const { virtualBrand, state, subscriptionModules, onStateChange } = this.props;

		return (
			<div className={styles.container}>
				<BrandDetailUrlState tabName={state.tab.name} virtualBranId={virtualBrand.id} virtualPackId={state.packId} />
				<div className={styles.content}>
					{state.packId !== null ? (
						<Message>
							{'Showing data only for selected package. '}
							<Button styles={MessageStyles} onClick={() => onStateChange({ ...state, packId: null })}>
								Select all
							</Button>
						</Message>
					) : null}

					<div className={styles.header}>
						<h1 className={styles.heading}>{virtualBrand.brandName}</h1>
						<div className={styles.headerSelect}>
							<span className={styles.headerSelectLabel}>Package:</span>
							<div className={styles.selectContainer}>
								<Select
									value={state.packId || ''}
									onChange={(event) =>
										onStateChange({
											...state,
											packId: event.target.value === '' ? null : event.target.value,
										})
									}
								>
									<option value=''>All packages</option>
									{virtualBrand.sortedVirtualPacks.map((virtualPack) => (
										<option key={virtualPack.id} value={virtualPack.id}>
											{virtualPack.distinctName} ({virtualPack.pricedActualPacks.length})
										</option>
									))}
								</Select>
							</div>
						</div>

						<div className={styles.routesOfAdministration}>
							Routes Of Administration:
							{virtualBrand.routesOfAdministration.map((route) => (
								<span className={styles.smallTag} key={route.name}>
									{route.name}
								</span>
							))}
						</div>
					</div>

					<div className={styles.tags}>
						<div>
							{virtualBrand.atcs.map((atc) => (
								<Fragment key={atc.code}>
									<span className={styles.tag}>{atc.code}</span>&nbsp;
									<span className={styles.tag}>{atc.activeSubstance}</span>
									&nbsp;
								</Fragment>
							))}
							{virtualBrand.generic ? <span className={styles.tag}>Generic</span> : null}
							{virtualBrand.biosimilar ? <span className={styles.tag}>Biosimilar</span> : null}
							{virtualBrand.orphan ? <span className={styles.tag}>Orphan</span> : null}
							{virtualBrand.originator ? <span className={styles.tag}>Originator</span> : null}
						</div>

						<div className={styles.therapeuticAreas}>
							{virtualBrand.therapeuticAreas.map((area) => (
								<span className={styles.smallTag} key={area.name}>
									{area.name}
								</span>
							))}
						</div>
					</div>

					<TabBar
						selectedTabName={state.tab.name}
						styles={tabBarStyles}
						onChange={(name: TabStateName.history | TabStateName.registration | TabStateName.info) =>
							onStateChange({ ...state, tab: { name, state: undefined } })
						}
					>
						<Tab name={TabStateName.info} styles={tabBarStyles}>
							Info
						</Tab>
						<Tab name={TabStateName.availability} styles={tabBarStyles}>
							Availability
						</Tab>
						{hasERPModuleActive(subscriptionModules) ? (
							<Tab name={TabStateName.irp} styles={tabBarStyles}>
								IRP
							</Tab>
						) : null}
						<Tab name={TabStateName.timeToPrice} styles={tabBarStyles}>
							Time to price
						</Tab>
						<Tab name={TabStateName.prices} styles={tabBarStyles}>
							Prices
						</Tab>
						<Tab name={TabStateName.reimbursement} styles={tabBarStyles}>
							Reimbursement
						</Tab>
						<Tab name={TabStateName.registration} styles={tabBarStyles}>
							Registration
						</Tab>
						<Tab name={TabStateName.history} styles={tabBarStyles}>
							History
						</Tab>
					</TabBar>

					{this.renderTab()}
				</div>
			</div>
		);
	}
}
